import { useEffect, useState } from 'react';
import { purchasingAPI, ReasonCodeAudit } from '../../../lib/api';
import { Badge } from '../../ui/badge';
import { Loader2, History } from 'lucide-react';
import { useFormatters } from '../../../contexts/PreferencesContext';

/**
 * CR-5886: renders the create / edit / archive audit trail for a single reason
 * code, showing who changed what and when.
 */
interface ReasonCodeAuditHistoryProps {
  reasonCodeId: number;
}

const ACTION_BADGE: Record<string, string> = {
  created: 'bg-blue-100 text-blue-700 hover:bg-blue-100',
  updated: 'bg-amber-100 text-amber-700 hover:bg-amber-100',
  archived: 'bg-rose-100 text-rose-700 hover:bg-rose-100',
  restored: 'bg-emerald-100 text-emerald-700 hover:bg-emerald-100',
};

// Actions where the badge already says everything — no field-diff list needed.
const SUMMARY_ONLY_ACTIONS = ['archived', 'restored'];

const FIELD_LABELS: Record<string, string> = {
  code: 'Code',
  description: 'Description',
  category: 'Category',
  is_active: 'Status',
  is_archived: 'Archived',
};

/** Render a field's value in human terms (the toggle reads Enabled/Disabled, not Yes/No). */
function formatValue(field: string, value: unknown): string {
  if (value === null || value === undefined || value === '') return '—';
  if (field === 'is_active') return value ? 'Enabled' : 'Disabled';
  if (field === 'is_archived') return value ? 'Archived' : 'Not archived';
  if (typeof value === 'boolean') return value ? 'Yes' : 'No';
  return String(value);
}

export function ReasonCodeAuditHistory({ reasonCodeId }: ReasonCodeAuditHistoryProps) {
  const [entries, setEntries] = useState<ReasonCodeAudit[]>([]);
  const [loading, setLoading] = useState(true);
  const { formatDateTime } = useFormatters();
  const [error, setError] = useState(false);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setError(false);
    purchasingAPI.getReasonCodeAudit(reasonCodeId)
      .then(res => { if (!cancelled) setEntries(res.data); })
      .catch(() => { if (!cancelled) setError(true); })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [reasonCodeId]);

  return (
    <div className="border-t pt-4">
      <div className="flex items-center gap-2 mb-3">
        <History className="h-4 w-4 text-muted-foreground" />
        <h4 className="text-sm font-semibold">Audit History</h4>
      </div>

      {loading ? (
        <div className="flex items-center justify-center py-4 text-muted-foreground">
          <Loader2 className="h-4 w-4 animate-spin" />
        </div>
      ) : error ? (
        <p className="text-sm text-muted-foreground">Could not load audit history.</p>
      ) : entries.length === 0 ? (
        <p className="text-sm text-muted-foreground">No audit history recorded.</p>
      ) : (
        <ul className="space-y-3 max-h-56 overflow-y-auto pr-1">
          {entries.map(entry => (
            <li key={entry.id} className="text-sm">
              <div className="flex items-center gap-2 flex-wrap">
                <Badge variant="secondary" className={ACTION_BADGE[entry.action] || ''}>
                  {entry.action_display}
                </Badge>
                <span className="font-medium">{entry.changed_by_name}</span>
                <span className="text-muted-foreground">
                  {formatDateTime(entry.changed_at)}
                </span>
              </div>
              {!SUMMARY_ONLY_ACTIONS.includes(entry.action) && Object.keys(entry.changes).length > 0 && (
                <ul className="mt-1 ml-1 text-xs text-muted-foreground space-y-0.5">
                  {Object.entries(entry.changes).map(([field, diff]) => (
                    <li key={field}>
                      <span className="font-medium text-foreground">
                        {FIELD_LABELS[field] || field}
                      </span>
                      {entry.action === 'created' ? (
                        <>: {formatValue(field, diff.new)}</>
                      ) : (
                        <>: {formatValue(field, diff.old)} → {formatValue(field, diff.new)}</>
                      )}
                    </li>
                  ))}
                </ul>
              )}
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}
